import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '../components/AppLayout';

const FAMILY_CARE_FORM_URL = 'https://readdy.ai/api/form/d8nsnl0jb57qogjbhnh0';

interface FormData {
  caregiver_name: string;
  caregiver_phone: string;
  caregiver_birth: string;
  caregiver_gender: string;
  patient_name: string;
  patient_phone: string;
  patient_birth: string;
  patient_gender: string;
  patient_address: string;
  guardian_name: string;
  guardian_phone: string;
  insurance_company: string;
  care_location: string;
  desired_start_date: string;
  notes: string;
}

const initialForm: FormData = {
  caregiver_name: '',
  caregiver_phone: '',
  caregiver_birth: '',
  caregiver_gender: '',
  patient_name: '',
  patient_phone: '',
  patient_birth: '',
  patient_gender: '',
  patient_address: '',
  guardian_name: '',
  guardian_phone: '',
  insurance_company: '',
  care_location: '',
  desired_start_date: '',
  notes: '',
};

export default function AppFamilyCarePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormData>(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const required: (keyof FormData)[] = [
      'caregiver_name', 'caregiver_phone', 'caregiver_birth',
      'patient_name', 'patient_phone', 'patient_birth', 'patient_gender', 'patient_address',
      'care_location',
    ];
    for (const field of required) {
      if (!form[field].trim()) {
        setError('필수 항목을 모두 입력해주세요.');
        return;
      }
    }

    if (!agreed) {
      setError('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.append('caregiver_name', form.caregiver_name);
      params.append('caregiver_phone', form.caregiver_phone);
      params.append('caregiver_birth', form.caregiver_birth);
      params.append('caregiver_gender', form.caregiver_gender);
      params.append('patient_name', form.patient_name);
      params.append('patient_phone', form.patient_phone);
      params.append('patient_birth', form.patient_birth);
      params.append('patient_gender', form.patient_gender);
      params.append('patient_address', form.patient_address);
      if (form.guardian_name.trim()) params.append('guardian_name', form.guardian_name);
      if (form.guardian_phone.trim()) params.append('guardian_phone', form.guardian_phone);
      if (form.insurance_company.trim()) params.append('insurance_company', form.insurance_company);
      params.append('care_location', form.care_location);
      params.append('desired_start_date', form.desired_start_date);
      params.append('notes', form.notes);

      await fetch(FAMILY_CARE_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      // 지역 간병인에게 푸쉬 알림 발송 (비동기, 실패해도 무시)
      if (form.patient_address) {
        supabase.functions.invoke('notify-caregivers', {
          body: {
            patient_address: form.patient_address,
            patient_name: form.patient_name || '익명',
            care_location: form.care_location || '',
            desired_start_date: form.desired_start_date || '',
          },
        }).catch(() => { /* 무시 */ });
      }

      setSubmitted(true);
    } catch {
      setError('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
    }

    setLoading(false);
  };

  const inputClass = 'w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#2E7D6B] focus:ring-1 focus:ring-[#2E7D6B] transition-colors bg-white placeholder-gray-300';
  const labelClass = 'block text-xs font-bold text-gray-600 mb-1.5';

  if (submitted) {
    return (
      <AppLayout>
        <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-white">
          <div className="w-16 h-16 rounded-2xl bg-[#E8F5E9] flex items-center justify-center mb-5">
            <i className="ri-check-line text-3xl text-[#2E7D6B]" />
          </div>
          <h1 className="text-xl font-extrabold text-gray-900 mb-2">가족간병 신청 완료!</h1>
          <p className="text-sm text-gray-500 text-center leading-relaxed mb-8">
            신청서가 정상적으로 접수되었습니다.<br />
            담당자가 확인 후 빠른 시일 내에 연락드리겠습니다.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/app')}
              className="px-6 py-3 rounded-xl text-sm font-bold text-[#2E7D6B] bg-[#E8F5E9] whitespace-nowrap cursor-pointer hover:opacity-80 transition-opacity"
            >
              대시보드로
            </button>
            <button
              onClick={() => {
                setSubmitted(false);
                setForm(initialForm);
                setAgreed(false);
              }}
              className="px-6 py-3 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              추가 신청하기
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="min-h-screen pb-24 bg-gray-50">
        {/* 헤더 */}
        <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
          <div className="px-5 py-4 flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-xl border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <i className="ri-arrow-left-line text-gray-500" />
            </button>
            <div>
              <h1 className="text-base font-extrabold text-gray-900">가족간병 신청</h1>
              <p className="text-[11px] text-gray-400">가족 간병 서비스를 신청합니다</p>
            </div>
          </div>
        </div>

        <div className="px-5 pt-5">
          <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">

            {/* 간병인 정보 */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
                  <i className="ri-user-star-line text-sm text-[#2E7D6B]" />
                </div>
                <h3 className="text-sm font-extrabold text-gray-900">간병인 정보</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>간병인 성명 <span className="text-red-400">*</span></label>
                  <input type="text" name="caregiver_name" value={form.caregiver_name} onChange={handleChange} placeholder="홍길동" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>간병인 연락처 <span className="text-red-400">*</span></label>
                  <input type="text" name="caregiver_phone" value={form.caregiver_phone} onChange={handleChange} placeholder="010-0000-0000" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>간병인 생년월일 (6자리) <span className="text-red-400">*</span></label>
                  <input type="text" name="caregiver_birth" value={form.caregiver_birth} onChange={handleChange} placeholder="500101" maxLength={6} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>희망 간병인 성별</label>
                  <select name="caregiver_gender" value={form.caregiver_gender} onChange={handleChange} className={inputClass}>
                    <option value="">선택하세요</option>
                    <option value="남">남</option>
                    <option value="여">여</option>
                    <option value="무관">무관</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 환자 정보 */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#FFF3E0] flex items-center justify-center">
                  <i className="ri-heart-pulse-line text-sm text-[#F57C00]" />
                </div>
                <h3 className="text-sm font-extrabold text-gray-900">환자 정보</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>환자 성명 <span className="text-red-400">*</span></label>
                  <input type="text" name="patient_name" value={form.patient_name} onChange={handleChange} placeholder="홍길순" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>환자 연락처 <span className="text-red-400">*</span></label>
                  <input type="text" name="patient_phone" value={form.patient_phone} onChange={handleChange} placeholder="010-0000-0000" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>환자 생년월일 (6자리) <span className="text-red-400">*</span></label>
                  <input type="text" name="patient_birth" value={form.patient_birth} onChange={handleChange} placeholder="500101" maxLength={6} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>환자 성별 <span className="text-red-400">*</span></label>
                  <select name="patient_gender" value={form.patient_gender} onChange={handleChange} className={inputClass}>
                    <option value="">선택하세요</option>
                    <option value="남">남</option>
                    <option value="여">여</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>환자 주소 <span className="text-red-400">*</span></label>
                  <input type="text" name="patient_address" value={form.patient_address} onChange={handleChange} placeholder="예) 서울특별시 강남구 테헤란로 123" className={inputClass} />
                </div>
              </div>
            </div>

            {/* 보호자 정보 */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#FCE4EC] flex items-center justify-center">
                  <i className="ri-user-heart-line text-sm text-[#C62828]" />
                </div>
                <h3 className="text-sm font-extrabold text-gray-900">보호자 정보</h3>
                <span className="text-[10px] text-gray-400">(선택사항)</span>
              </div>
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>보호자 성명</label>
                  <input type="text" name="guardian_name" value={form.guardian_name} onChange={handleChange} placeholder="이름을 입력하세요" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>보호자 연락처</label>
                  <input type="text" name="guardian_phone" value={form.guardian_phone} onChange={handleChange} placeholder="010-0000-0000" className={inputClass} />
                </div>
              </div>
            </div>

            {/* 가입 보험사 */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#E8EAF6] flex items-center justify-center">
                  <i className="ri-building-2-line text-sm text-[#283593]" />
                </div>
                <h3 className="text-sm font-extrabold text-gray-900">가입 보험사</h3>
              </div>
              <div>
                <label className={labelClass}>보험사명</label>
                <input type="text" name="insurance_company" value={form.insurance_company} onChange={handleChange} placeholder="예: 삼성생명, DB손해보험 등" className={inputClass} />
              </div>
            </div>

            {/* 간병 희망 정보 */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#E3F2FD] flex items-center justify-center">
                  <i className="ri-calendar-check-line text-sm text-[#1976D2]" />
                </div>
                <h3 className="text-sm font-extrabold text-gray-900">간병 희망 정보</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>간병 장소 <span className="text-red-400">*</span></label>
                  <select name="care_location" value={form.care_location} onChange={handleChange} className={inputClass}>
                    <option value="">선택하세요</option>
                    <option value="병원">병원</option>
                    <option value="집">집</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>희망 시작일</label>
                  <input type="date" name="desired_start_date" value={form.desired_start_date} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>요청사항 / 특이사항</label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    placeholder="간병 시 특별히 필요한 사항이나 요청사항을 적어주세요 (예: 야간 간병 필요, 남자 간병인 선호 등)"
                    maxLength={500}
                    rows={3}
                    className={`${inputClass} resize-none`}
                  />
                  <p className="text-[10px] text-gray-400 mt-1 text-right">{form.notes.length}/500</p>
                </div>
              </div>
            </div>

            {/* 개인정보 동의 */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="w-5 h-5 rounded-md border-gray-300 text-[#2E7D6B] focus:ring-[#2E7D6B] mt-0.5 shrink-0 cursor-pointer"
                />
                <span className="text-xs text-gray-500 leading-relaxed">
                  개인정보 수집 및 이용에 동의합니다. 수집된 정보는 간병 서비스 매칭 목적으로만 사용되며, 서비스 종료 후 안전하게 파기됩니다.
                </span>
              </label>
            </div>

            {/* 에러 메시지 */}
            {error && (
              <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-100 rounded-xl">
                <i className="ri-error-warning-line text-red-400 text-sm" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* 제출 버튼 */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-2xl text-base font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />신청 중...
                </span>
              ) : (
                '가족간병 신청하기'
              )}
            </button>

            <p className="text-[10px] text-gray-400 text-center pb-8">
              신청 후 담당자가 확인하여 빠른 시일 내에 연락드립니다
            </p>
          </form>
        </div>
      </div>
    </AppLayout>
  );
}